import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const generateFYOptions = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const options = [];
  const startFY = currentMonth >= 3 ? currentYear : currentYear - 1;
  for (let i = 0; i < 6; i++) {
    const fy = startFY - i;
    options.push({
      label: `FY ${fy}-${(fy + 1).toString().slice(-2)}`,
      start: `${fy}-04-01`,
      end: `${fy + 1}-03-31`
    });
  }
  return options;
};

const ProfitLoss = () => {
  const [report, setReport] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFY, setSelectedFY] = useState('');
  const [loading, setLoading] = useState(false);
  const fyOptions = generateFYOptions();

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await apiClient.get('/api/accounts/profit-loss', { params });
      if (response.data) {
        setReport(response.data);
      }
    } catch (error) {
      console.error('Error fetching P&L report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFYChange = (value) => {
    setSelectedFY(value);
    if (value === '') {
      setStartDate('');
      setEndDate('');
    } else {
      const fy = fyOptions.find(f => f.label === value);
      if (fy) {
        setStartDate(fy.start);
        setEndDate(fy.end);
      }
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-6 h-6" />Profit & Loss Statement</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-3 items-end mb-6 flex-wrap" data-testid="pnl-filters">
            <div>
              <Label>Financial Year</Label>
              <select
                value={selectedFY}
                onChange={(e) => handleFYChange(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="pnl-fy"
              >
                <option value="">All Time</option>
                {fyOptions.map(fy => (
                  <option key={fy.label} value={fy.label}>{fy.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <Label>From Date</Label>
              <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setSelectedFY(''); }} data-testid="pnl-from-date" />
            </div>
            <div className="flex-1">
              <Label>To Date</Label>
              <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setSelectedFY(''); }} data-testid="pnl-to-date" />
            </div>
            <Button onClick={fetchReport} disabled={loading} data-testid="pnl-generate-btn">
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>

          {report && (
            <div className="space-y-6">
              <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />Revenue
                      </h3>
                      <div className="space-y-2">
                        {report.income_by_category && Object.entries(report.income_by_category).map(([cat, amount]) => (
                          <div key={cat} className="flex justify-between text-sm">
                            <span className="text-gray-600">{cat}</span>
                            <span className="font-medium text-green-600">₹{amount?.toFixed(2)}</span>
                          </div>
                        ))}
                        {(!report.income_by_category || Object.keys(report.income_by_category).length === 0) && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Income:</span>
                            <span className="font-semibold text-green-600">₹{report.total_income?.toFixed(2) || '0.00'}</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t mt-4 pt-2">
                        <div className="flex justify-between">
                          <span className="font-semibold">Gross Revenue:</span>
                          <span className="font-bold text-green-700">₹{report.total_income?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <TrendingDown className="w-5 h-5 text-red-600" />Expenses
                      </h3>
                      <div className="space-y-2">
                        {report.expense_by_category && Object.entries(report.expense_by_category).map(([cat, amount]) => (
                          <div key={cat} className="flex justify-between text-sm">
                            <span className="text-gray-600">{cat}</span>
                            <span className="font-medium text-red-600">₹{amount?.toFixed(2)}</span>
                          </div>
                        ))}
                        {(!report.expense_by_category || Object.keys(report.expense_by_category).length === 0) && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Expenses:</span>
                            <span className="font-semibold text-red-600">₹{report.total_expense?.toFixed(2) || '0.00'}</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t mt-4 pt-2">
                        <div className="flex justify-between">
                          <span className="font-semibold">Total Expenses:</span>
                          <span className="font-bold text-red-700">₹{report.total_expense?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t-2 mt-6 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Gross Profit:</span>
                      <span className={`text-2xl font-bold ${(report.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>₹{report.gross_profit?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>

                  <div className="border-t mt-4 pt-4 bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Net Profit/Loss:</span>
                      <span className={`text-3xl font-bold ${(report.net_profit || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                        {(report.net_profit || 0) >= 0 ? '+' : ''}₹{report.net_profit?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2 text-right">
                      {(report.net_profit || 0) >= 0 ? 'Profit' : 'Loss'} for the period
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="text-sm text-gray-600">
                <p>• Gross Profit = Total Income - Direct Expenses</p>
                <p>• Net Profit = Gross Profit - Operating Expenses</p>
              </div>
            </div>
          )}
          
          {!report && !loading && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Click "Generate" to view the Profit & Loss report</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfitLoss;
